import { useState } from "preact/hooks"
import { Input } from "../../../components/ui/input"
import type { TimePeriod } from "../types"
import { parse_cell_input } from "../utils/cell_input"
import { door_schedule } from "../utils/line_setup"
import { format_period_header } from "../utils/periods"

interface DoorsByMonthEditorProps {
  initial_doors: number
  /** Store-count growth per year. */
  store_growth: number
  start_period: string
  periods: TimePeriod[]
  doors_by_period: Record<string, number>
  on_change: (doors_by_period: Record<string, number>) => void
  className?: string
}

/**
 * One box per month from the line's start: the doors growth gives, or the count typed for that
 * month (highlighted). Clearing a box hands the month back to growth. The same override a door
 * edit on the grid makes, before the line exists (1st Phorm, 10/29).
 */
export function DoorsByMonthEditor(props: DoorsByMonthEditorProps) {
  const { initial_doors, store_growth, start_period, periods, doors_by_period, on_change, className } = props
  const [error, set_error] = useState<string | null>(null)
  const schedule = door_schedule({ initial_doors, store_growth, start_period, doors_by_period }, periods)
  if (schedule.length === 0) {
    return <p className={`text-[11px] text-gray-500 ${className ?? ""}`}>Pick a start month to set doors by month.</p>
  }
  const by_key = new Map(periods.map((period: TimePeriod) => [period.period_key, period]))

  function handle_input(period_key: string, raw: string): void {
    const next = { ...doors_by_period }
    if (raw.trim() === "") {
      delete next[period_key]
      set_error(null)
      on_change(next)
      return
    }
    const parsed = parse_cell_input(raw, "store_count")
    if (!parsed.ok) {
      set_error(parsed.message)
      return
    }
    set_error(null)
    next[period_key] = parsed.value
    on_change(next)
  }

  return (
    <div className={className}>
      <div className="flex gap-1 overflow-x-auto pb-1">
        {schedule.map((entry) => {
          const period = by_key.get(entry.period_key)
          return (
            <label key={entry.period_key} className="flex w-16 shrink-0 flex-col items-center gap-0.5 text-[10px] text-gray-500">
              {period ? format_period_header(period) : entry.period_key}
              <Input
                type="number"
                step="1"
                value={entry.overridden ? String(entry.doors) : ""}
                placeholder={String(entry.doors)}
                onInput={(e: Event) => handle_input(entry.period_key, (e.currentTarget as HTMLInputElement).value)}
                className={`h-7 w-16 px-1 text-center text-xs ${entry.overridden ? "border-blue-400 bg-blue-50 font-medium" : ""}`}
                aria-label={`Doors in ${entry.period_key}`}
              />
            </label>
          )
        })}
      </div>
      {error ? (
        <p role="alert" className="text-[11px] text-red-600">
          {error}
        </p>
      ) : (
        <p className="text-[11px] text-gray-500">Grey numbers follow store growth; type a month to set its doors, clear it to follow growth again.</p>
      )}
    </div>
  )
}
